import { Card, H1, H3 } from '@/components'
import { Checkbox, MonthPicker, UserSelect } from '@/components_deprecated'
import { LeaderGroupSelect } from '@/components_deprecated/Select/LeaderGroupSelect'
import { Field } from '@/modules/companies'
import { ALL_CARD_INSERT_PERIODS_START_STOP_COUNTRY } from '../queries'
import { format } from '@/utils'
import { getNodes } from '@/utils/graphql'
import { usePermissions, useUser, useLeaderGroups } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  endOfMonth,
  formatISO,
  isSameDay,
  parseISO,
  startOfMonth,
} from 'date-fns'
import React from 'react'
import { isMobileOnly } from 'react-device-detect'

export const StartStopCountryRegistrations: React.FC = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    period: 'common.period',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',
    manualRegistrations: 'activities.manual-registrations',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    chooseGroup: 'common.choose-group',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    confirmHours: 'salary.confirm-hours',

    showResolved: 'salary.show-resolved',
    showOk: 'activities.show-ok',

    leaderGroups: 'companies.leader-groups',
    noManualRegistration: 'salary.no-manual-registration',
  })

  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.workPeriod.view.othersworkperiod,
    true
  )

  const { hasAccess, myLeaderGroups, groupsLoading } = useLeaderGroups()

  const form = useForm<{
    user: string | null
    datetimeStart: Date
    datetimeEnd: Date
    showResolved: boolean
    leaderGroup: string | null
    leaderGroupsUsers: string[]
  }>({
    values: {
      user: null,
      datetimeStart: startOfMonth(new Date()),
      datetimeEnd: endOfMonth(new Date()),
      showResolved: false,
      leaderGroup: null,
      leaderGroupsUsers: [],
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'startStopCountryRegistrations',
        storeFormStateKey: 'startStopCountryRegistrations',
        transformValues: {
          user: val => {
            return val && canSeeOthers ? String(val) : null
          },
          datetimeStart: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          datetimeEnd: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  const { data } = useQuery(ALL_CARD_INSERT_PERIODS_START_STOP_COUNTRY, {
    variables: {
      userId: form.formValues.user ?? (canSeeOthers ? null : me.id),
      dateStart: formatISO(form.formValues.datetimeStart),
      dateEnd: formatISO(form.formValues.datetimeEnd),
    },
  })

  const cardInsertPeriods = getNodes(data?.allCardInsertPeriods)

  const displayCardInsertPeriods = cardInsertPeriods.filter(period => {
    if (
      !!period.startCountry &&
      !!period.stopCountry &&
      !form.formValues.showResolved
    ) {
      return false
    }
    const user = form.formValues.user
    if (user && form.formValues.leaderGroup) {
      return (
        form.formValues.leaderGroupsUsers.includes(user) &&
        form.formValues.leaderGroupsUsers.includes(period.user.id)
      )
    }
    if (user || !form.formValues.leaderGroup) return true
    return form.formValues.leaderGroupsUsers.includes(period.user.id)
  })

  const changeLeaderGroup = (value: string | null) => {
    const selectedGroup = myLeaderGroups.find(g => g.id === value)
    form.updateForm({
      leaderGroup: value,
      leaderGroupsUsers: selectedGroup?.users.map(user => user.id) ?? [],
    })
  }

  return (
    <div className="relative box-border px-4 py-4 md:px-[52px] md:py-8 md:pb-[52px]">
      <div className="flex gap-2">
        <H1>{translations.manualRegistrations} </H1>
      </div>
      <Card
        className="mb-4 p-0"
        contentClassName="grid grid-cols-1 md:grid-cols-[1fr_auto]"
      >
        <section className="grid grid-cols-1 gap-4 p-4 md:grid-cols-[1fr_auto_auto_auto]">
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserSelect
                value={form.formValues.user}
                grayed
                width="100%"
                minWidth={isMobileOnly ? '0' : '260px'}
                height="3.875rem"
                onChange={user => form.updateForm({ user: user ?? null })}
                filterSalaryResponsible={false}
                filterLeaderGroup={true}
                nullable
              />
            </Field>
          )}
          {hasAccess && (
            <Field>
              <label>{translations.leaderGroups}</label>
              <LeaderGroupSelect
                value={form.formValues.leaderGroup}
                loading={groupsLoading}
                options={
                  myLeaderGroups?.map(group => ({
                    label: group.name,
                    value: group.id,
                  })) ?? []
                }
                fullWidth
                grayed={true}
                onChange={value => changeLeaderGroup(value)}
                placeholder={translations.chooseGroup}
                nullable
              />
            </Field>
          )}

          <Field>
            <label>{translations.showOk}</label>
            <Checkbox
              checked={form.formValues.showResolved}
              onChange={checked => form.updateForm({ showResolved: checked })}
              boxSize="3.875rem"
              padding="2rem"
              boxBorderRadius="8px"
            />
          </Field>
        </section>
        <section className="grid grid-cols-1 items-center p-4 pt-0 md:border-l md:border-[#E1E1E1] md:pt-4">
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={form.formValues.datetimeStart}
              onChange={date => {
                const start = startOfMonth(date)
                const end = endOfMonth(date)
                form.updateForm({
                  datetimeStart: start,
                  datetimeEnd: end,
                })
              }}
            />
          </Field>
        </section>
      </Card>

      <div className="flex flex-col gap-2">
        {displayCardInsertPeriods.map(period => {
          // datetimeStart/datetimeEnd are the driver card's calendar-day
          // slices, so they read 00:00 - 23:59 for a card left in the vehicle
          // overnight. The work period is when the driver actually started
          // and stopped.
          const startDate = parseISO(
            period.workPeriodStart ?? period.datetimeStart
          )
          const endDate = parseISO(period.workPeriodEnd ?? period.datetimeEnd)
          const isSameDayValue = isSameDay(startDate, endDate)
          const hasStartAndStopCountry =
            !!period.startCountry && !!period.stopCountry

          return (
            <Card
              key={period.id}
              className={`border border-[#E1E1E1] px-5 py-4 ${
                hasStartAndStopCountry ? 'bg-[#EAFBD7]' : 'bg-white'
              }`}
            >
              <div>
                <H3>{period.user.fullName}</H3>
                <div>
                  <strong>{translations.period}: </strong>{' '}
                  {format(startDate, 'dd.MM.yyyy HH:mm')} -{' '}
                  {isSameDayValue
                    ? format(endDate, 'HH:mm')
                    : format(endDate, 'dd.MM.yyyy HH:mm')}
                </div>
                <div className="flex gap-1">
                  <strong>{translations.manualRegistrations}: </strong>
                  <div
                    className={
                      period.startCountry
                        ? 'text-[#53B734]'
                        : 'text-[#fa1852] italic'
                    }
                  >
                    {period.startCountry ? period.startCountry : 'N/A'}
                  </div>
                  -
                  <div
                    className={
                      period.stopCountry
                        ? 'text-[#53B734]'
                        : 'text-[#fa1852] italic'
                    }
                  >
                    {period.stopCountry ? period.stopCountry : 'N/A'}
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
